import React, { useState } from 'react';
import { Pressable } from 'react-native';
import { SafeBlurView as BlurView } from '~/components/ui/SafeBlurView';
import { Menu, MenuItem, MenuItemLabel, MenuSeparator } from '@/components/gluestack/menu';
import { Box } from '@/components/gluestack/box';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import {
  MoreVerticalIcon,
  Cancel01Icon,
} from '@hugeicons-pro/core-solid-rounded';
import {
  Alert02Icon,
  UnavailableIcon,
  CheckmarkSquare02Icon,
  SquareLock02Icon,
  ViewOffSlashIcon,
} from '@hugeicons-pro/core-stroke-rounded';
import { MenuItemIcon } from '@/components/ui/MenuItemIcon';
import { MemberDetails } from '@/models/MemberDetails';
import { plainToClass } from '@/models/transform';
import { useTranslation } from 'react-i18next';
import { useSupportService } from '@/hooks/useSupportService';
import { ReportReasonSheet } from '@/components/domain/common/ReportReasonSheet';
import { MembersService } from '@/services/domain/MembersService';
import { useToastMessages } from '@/hooks/useToastMessages';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogCloseButton,
  AlertDialogBody,
  AlertDialogFooter,
} from '@/components/gluestack/alert-dialog';
import { Button, ButtonText } from '@/components/gluestack/button';
import { Text } from '@/components/gluestack/text';
import { Heading } from '@/components/gluestack/heading';

interface ProfileContextMenuProps {
  member: MemberDetails;
}

export function ProfileContextMenu({ member }: ProfileContextMenuProps) {
  const { t } = useTranslation();
  const { submitPolicyViolationForMember } = useSupportService();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [showBlockConfirmation, setShowBlockConfirmation] = useState(false);
  const [showUnblockConfirmation, setShowUnblockConfirmation] = useState(false);
  const [showRevokePrivatePhotosConfirmation, setShowRevokePrivatePhotosConfirmation] = useState(false);
  const [showForfeitPrivatePhotosConfirmation, setShowForfeitPrivatePhotosConfirmation] = useState(false);

  const updateMemberDetailsCache = (patch: Partial<MemberDetails>) => {
    queryClient.setQueryData(['member-details', member.UserName], (oldData: MemberDetails | undefined) => {
      if (!oldData) return oldData;
      // Rehydrate as MemberDetails so the prototype getters (PlaceNameCleaned,
      // PlaceNameShort, FirstNameCleaned, etc.) survive the cache update.
      return plainToClass(MemberDetails, { ...oldData, ...patch });
    });
  };

  const [showReportReasonSheet, setShowReportReasonSheet] = useState(false);

  const handleReport = () => {
    setShowReportReasonSheet(true);
  };

  const handleShowBlockConfirmation = () => {
    setShowBlockConfirmation(true);
  };

  const handleShowUnblockConfirmation = () => {
    setShowUnblockConfirmation(true);
  };

  const handleBlock = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await MembersService.blockMember(member.Id);

      updateMemberDetailsCache({ IsBlocked: true });

      showSuccessToast(t('member.block.success'));
    } catch (error) {
      showErrorToast(t('member.block.error'), error);
    } finally {
      setIsLoading(false);
      setShowBlockConfirmation(false);
    }
  };

  const handleUnblock = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await MembersService.unblockMember(member.Id);

      updateMemberDetailsCache({ IsBlocked: false });

      showSuccessToast(t('member.unblock.success'));
    } catch (error) {
      showErrorToast(t('member.unblock.error'), error);
    } finally {
      setIsLoading(false);
      setShowUnblockConfirmation(false);
    }
  };

  const handleRevokePrivatePhotos = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await MembersService.removePhotoUnlock(member.Id);

      updateMemberDetailsCache({ IsAuthorizedViewer: false });

      showSuccessToast(t('member.menu.revokePrivatePhotos.success'));
    } catch (error) {
      showErrorToast(t('member.menu.revokePrivatePhotos.error'), error);
    } finally {
      setIsLoading(false);
      setShowRevokePrivatePhotosConfirmation(false);
    }
  };

  const handleForfeitPrivatePhotos = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await MembersService.forfeitPhotoUnlock(member.Id);

      updateMemberDetailsCache({ IsPrivateFolderUnlocked: false });

      showSuccessToast(t('member.menu.forfeitPrivatePhotos.success'));
    } catch (error) {
      showErrorToast(t('member.menu.forfeitPrivatePhotos.error'), error);
    } finally {
      setIsLoading(false);
      setShowForfeitPrivatePhotosConfirmation(false);
    }
  };

  return (
    <>
      <Menu
        placement="bottom right"
        offset={10}
        trigger={(props: any) => (
          <Box className="self-start rounded-full overflow-hidden">
            <BlurView
              intensity={80}
              tint="dark"
              className="p-1">
              <Pressable
                {...props}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                <HugeIconsWrapper icon={MoreVerticalIcon} size={28} className="text-white" />
              </Pressable>
            </BlurView>
          </Box>
        )}>
        
        {(member.IsAuthorizedViewer || member.IsPrivateFolderUnlocked) && (
          <MenuItem
            key="private-photos-section"
            textValue={t('member.menu.privatePhotosSection')}
            disabled
            className="bg-transparent data-[hover=true]:bg-transparent data-[active=true]:bg-transparent data-[focus=true]:bg-transparent">
            <MenuItemLabel className="text-sm font-bold text-typography-500 uppercase">
              {t('member.menu.privatePhotosSection')}
            </MenuItemLabel>
          </MenuItem>
        )}

        {member.IsAuthorizedViewer && (
          <MenuItem
            key="revoke-private-photos"
            textValue={t('member.menu.revokePrivatePhotos.action')}
            onPress={() => setShowRevokePrivatePhotosConfirmation(true)}
            disabled={isLoading}>
            <MenuItemIcon icon={SquareLock02Icon} />
            <MenuItemLabel>{t('member.menu.revokePrivatePhotos.action')}</MenuItemLabel>
          </MenuItem>
        )}

        {member.IsPrivateFolderUnlocked && (
          <MenuItem
            key="forfeit-private-photos"
            textValue={t('member.menu.forfeitPrivatePhotos.action')}
            onPress={() => setShowForfeitPrivatePhotosConfirmation(true)}
            disabled={isLoading}>
            <MenuItemIcon icon={ViewOffSlashIcon} />
            <MenuItemLabel>{t('member.menu.forfeitPrivatePhotos.action')}</MenuItemLabel>
          </MenuItem>
        )}

        {(member.IsAuthorizedViewer || member.IsPrivateFolderUnlocked) && (
          <MenuSeparator className="my-2" />
        )}

        <MenuItem key="report" textValue={t('member.menu.report')} onPress={handleReport}>
          <MenuItemIcon icon={Alert02Icon} className="color-error-500" />
          <MenuItemLabel>{t('member.menu.report')}</MenuItemLabel>
        </MenuItem>

        {member.IsBlocked ? (
          <MenuItem
            key="unblock"
            textValue={t('member.menu.unblock')}
            onPress={handleShowUnblockConfirmation}
            disabled={isLoading}>
            <MenuItemIcon icon={CheckmarkSquare02Icon} />
            <MenuItemLabel>{t('member.menu.unblock')}</MenuItemLabel>
          </MenuItem>
        ) : (
          <MenuItem
            key="block"
            textValue={t('member.menu.block')}
            onPress={handleShowBlockConfirmation}
            disabled={isLoading}>
            <MenuItemIcon icon={UnavailableIcon} className="color-error-500" />
            <MenuItemLabel>{t('member.menu.block')}</MenuItemLabel>
          </MenuItem>
        )}
      </Menu>

    {/* Block Confirmation Dialog */}
    <AlertDialog
      isOpen={showBlockConfirmation}
      onClose={() => setShowBlockConfirmation(false)}
      size="md">
      <AlertDialogBackdrop />
      <AlertDialogContent>
        <AlertDialogHeader>
          <Heading size="md">{t('member.menu.block')}</Heading>
          <AlertDialogCloseButton>
            <HugeIconsWrapper icon={Cancel01Icon} size={20} className="color-neutral-500" />
          </AlertDialogCloseButton>
        </AlertDialogHeader>
        <AlertDialogBody className="mt-3">
          <Text>{t('member.block.confirmMessage', { userName: member.UserName })}</Text>
        </AlertDialogBody>
        <AlertDialogFooter className="mt-6 gap-3">
          <Button
            variant="outline"
            action="neutral"
            onPress={() => setShowBlockConfirmation(false)}
            disabled={isLoading}>
            <ButtonText>{t('common.cancel')}</ButtonText>
          </Button>
          <Button action="negative" onPress={handleBlock} disabled={isLoading}>
            <ButtonText>{t('common.continue')}</ButtonText>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Unblock Confirmation Dialog */}
    <AlertDialog
      isOpen={showUnblockConfirmation}
      onClose={() => setShowUnblockConfirmation(false)}
      size="md">
      <AlertDialogBackdrop />
      <AlertDialogContent>
        <AlertDialogHeader>
          <Heading size="md">{t('member.menu.unblock')}</Heading>
          <AlertDialogCloseButton>
            <HugeIconsWrapper icon={Cancel01Icon} size={20} className="color-neutral-500" />
          </AlertDialogCloseButton>
        </AlertDialogHeader>
        <AlertDialogBody className="mt-3">
          <Text>{t('member.unblock.confirmMessage', { userName: member.UserName })}</Text>
        </AlertDialogBody>
        <AlertDialogFooter className="mt-6 gap-3">
          <Button
            variant="outline"
            action="neutral"
            onPress={() => setShowUnblockConfirmation(false)}
            disabled={isLoading}>
            <ButtonText>{t('common.cancel')}</ButtonText>
          </Button>
          <Button action="primary" onPress={handleUnblock} disabled={isLoading}>
            <ButtonText>{t('common.continue')}</ButtonText>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Revoke Private Photos Access Confirmation Dialog (owner side) */}
    <AlertDialog
      isOpen={showRevokePrivatePhotosConfirmation}
      onClose={() => setShowRevokePrivatePhotosConfirmation(false)}
      size="md">
      <AlertDialogBackdrop />
      <AlertDialogContent>
        <AlertDialogHeader>
          <Heading size="md">{t('member.menu.revokePrivatePhotos.confirmTitle')}</Heading>
          <AlertDialogCloseButton>
            <HugeIconsWrapper icon={Cancel01Icon} size={20} className="color-neutral-500" />
          </AlertDialogCloseButton>
        </AlertDialogHeader>
        <AlertDialogBody className="mt-3">
          <Text>{t('member.menu.revokePrivatePhotos.confirmMessage', { userName: member.UserName })}</Text>
        </AlertDialogBody>
        <AlertDialogFooter className="mt-6 gap-3">
          <Button
            variant="outline"
            action="neutral"
            onPress={() => setShowRevokePrivatePhotosConfirmation(false)}
            disabled={isLoading}>
            <ButtonText>{t('common.cancel')}</ButtonText>
          </Button>
          <Button action="primary" onPress={handleRevokePrivatePhotos} disabled={isLoading}>
            <ButtonText>{t('common.continue')}</ButtonText>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Forfeit Private Photos Access Confirmation Dialog (viewer side) */}
    <AlertDialog
      isOpen={showForfeitPrivatePhotosConfirmation}
      onClose={() => setShowForfeitPrivatePhotosConfirmation(false)}
      size="md">
      <AlertDialogBackdrop />
      <AlertDialogContent>
        <AlertDialogHeader>
          <Heading size="md">{t('member.menu.forfeitPrivatePhotos.confirmTitle')}</Heading>
          <AlertDialogCloseButton>
            <HugeIconsWrapper icon={Cancel01Icon} size={20} className="color-neutral-500" />
          </AlertDialogCloseButton>
        </AlertDialogHeader>
        <AlertDialogBody className="mt-3">
          <Text>{t('member.menu.forfeitPrivatePhotos.confirmMessage', { userName: member.UserName })}</Text>
        </AlertDialogBody>
        <AlertDialogFooter className="mt-6 gap-3">
          <Button
            variant="outline"
            action="neutral"
            onPress={() => setShowForfeitPrivatePhotosConfirmation(false)}
            disabled={isLoading}>
            <ButtonText>{t('common.cancel')}</ButtonText>
          </Button>
          <Button action="primary" onPress={handleForfeitPrivatePhotos} disabled={isLoading}>
            <ButtonText>{t('common.continue')}</ButtonText>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
      <ReportReasonSheet
        isOpen={showReportReasonSheet}
        onClose={() => setShowReportReasonSheet(false)}
        subject="member"
        onReasonSelected={(reason) => submitPolicyViolationForMember(member, reason)}
      />
    </>
  );
} 